import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone, PenTool, FileText, Ruler, Home, Building2, Clock } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const SketchToCAD = () => {
  const faqs = [
    {
      question: "What is Sketch to CAD?",
      answer: "Sketch to CAD is our service converting hand-drawn sketches, site measurements and existing plans into accurate, professional CAD drawings. We take your rough sketches, measure from site, or work from old plans to produce digital floor plans, elevations and sections in AutoCAD or PDF format."
    },
    {
      question: "What do I need to provide?",
      answer: "You can provide hand-drawn sketches with dimensions, photographs of the property, existing paper plans (even old or damaged ones), or we can arrange a measured survey. The more information you provide, the more accurate the output. We'll clarify any ambiguities before finalising."
    },
    {
      question: "What formats do you deliver?",
      answer: "We deliver in AutoCAD DWG format (industry standard, editable), PDF for easy viewing and printing, and can also provide DXF or other formats on request. All drawings are to scale and include dimensions, room labels and key annotations."
    },
    {
      question: "How long does it take?",
      answer: "Standard turnaround is 3-5 working days from receiving complete information. Rush service (24-48 hours) available for urgent projects. Complex properties or large developments may take longer—we'll confirm timescales when quoting."
    },
    {
      question: "Can you survey the property for me?",
      answer: "Yes, we offer measured surveys across the South West. We'll visit your property, take all necessary measurements, photographs and notes, then produce accurate CAD drawings. Survey fees depend on property size and location."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Sketch to CAD | Drawing Conversion | Floor Plans | AutoCAD Drawings | Energy Performance Direct</title>
        <meta name="description" content="Professional sketch to CAD conversion service. Hand-drawn plans to AutoCAD floor plans, elevations and sections. Measured surveys available. From £100. Call 01392 980077." />
        <meta name="keywords" content="sketch to CAD, drawing conversion, floor plan CAD, AutoCAD drawings, measured survey, plan digitalisation, hand drawn to CAD" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/sketch-to-cad" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        <section className="py-16 bg-gradient-to-br from-purple-50 via-indigo-50 to-purple-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block mb-4 px-4 py-2 bg-white rounded-full shadow-sm">
                <span className="text-sm font-medium text-purple-600">Drawing Conversion Service</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Sketch to CAD
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Professional conversion of hand-drawn sketches and existing plans into accurate, scalable CAD drawings. Floor plans, elevations and sections in AutoCAD format.
              </p>
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <CheckCircle className="h-5 w-5 text-purple-600" />
                  <span className="text-sm font-medium">From £100</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <CheckCircle className="h-5 w-5 text-purple-600" />
                  <span className="text-sm font-medium">AutoCAD DWG & PDF</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <CheckCircle className="h-5 w-5 text-purple-600" />
                  <span className="text-sm font-medium">3-5 Day Turnaround</span>
                </div>
              </div>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/booking">
                  <Button size="lg" className="bg-purple-600 hover:bg-purple-700">
                    Request CAD Quote
                  </Button>
                </Link>
                <a href="tel:01392980077">
                  <Button size="lg" variant="outline" className="border-purple-600 text-purple-600 hover:bg-purple-50">
                    <Phone className="mr-2 h-5 w-5" />
                    01392 980077
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">Sketch to CAD Services</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <PenTool className="h-12 w-12 text-purple-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Sketch Conversion</h3>
                    <p className="text-gray-600">
                      Convert your hand-drawn sketches into professional CAD drawings. Perfect for initial design ideas, rough layouts and concept plans.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <FileText className="h-12 w-12 text-purple-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Plan Digitalisation</h3>
                    <p className="text-gray-600">
                      Convert old paper plans, architect drawings or scanned documents into editable CAD format. Restore damaged or faded plans.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Ruler className="h-12 w-12 text-purple-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Measured Surveys</h3>
                    <p className="text-gray-600">
                      On-site measured surveys to capture accurate dimensions. We visit, measure and produce CAD drawings from scratch.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Home className="h-12 w-12 text-purple-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Residential Plans</h3>
                    <p className="text-gray-600">
                      Floor plans for houses, flats, bungalows and apartments. Suitable for estate agents, EPCs, planning applications and property records.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Building2 className="h-12 w-12 text-purple-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Commercial Plans</h3>
                    <p className="text-gray-600">
                      Floor plans for offices, retail, warehouses and industrial units. Lease plans, marketing plans and facilities management drawings.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Clock className="h-12 w-12 text-purple-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Rush Service</h3>
                    <p className="text-gray-600">
                      Urgent deadline? We offer 24-48 hour rush service for time-critical projects. Contact us for availability and pricing.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">The Process</h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                  <div>
                    <h3 className="font-semibold text-lg">Submit Your Materials</h3>
                    <p className="text-gray-600">Send us your sketches, photographs, existing plans or request a measured survey. Email, post or upload—whatever's easiest.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                  <div>
                    <h3 className="font-semibold text-lg">Review & Quote</h3>
                    <p className="text-gray-600">We review your materials, clarify any queries and provide a fixed-price quote. For surveys, we'll arrange a convenient time to visit.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                  <div>
                    <h3 className="font-semibold text-lg">CAD Production</h3>
                    <p className="text-gray-600">Our technicians create accurate, scaled CAD drawings from your materials. We follow industry standards and include all relevant details.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">4</div>
                  <div>
                    <h3 className="font-semibold text-lg">Delivery & Revisions</h3>
                    <p className="text-gray-600">Receive your drawings in DWG and PDF format. Minor revisions included—we want you to be completely satisfied with the result.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">Sketch to CAD FAQs</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <Card key={index}>
                    <CardContent className="pt-6">
                      <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                      <p className="text-gray-600">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-purple-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Need CAD Drawings?</h2>
            <p className="text-xl mb-8 opacity-90">
              Professional sketch to CAD conversion and measured surveys
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/booking">
                <Button size="lg" variant="secondary" className="bg-white text-purple-600 hover:bg-gray-100">
                  Request Quote
                </Button>
              </Link>
              <a href="tel:01392980077">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-purple-700">
                  <Phone className="mr-2 h-5 w-5" />
                  01392 980077
                </Button>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default SketchToCAD;
